import { useEffect, useState } from "react"
import { TextField, type TextFieldProps } from "@mui/material"

/**
 * A port count on the licence builder (2026-11-02).
 *
 * The box used to be `type="number"` bound straight to the number in the
 * draft, with an empty value turned into 0 on every keystroke — so clearing
 * it put the 0 straight back and typing 10 after it read "010" (CEO, reported
 * on the reseller portal; the same code was here).
 *
 * The field keeps its own TEXT while you type: it may be empty, it holds
 * digits only, and a leading zero is dropped ("010" → "10"). The draft still
 * gets a whole number (empty counts as 0), and leaving an empty box shows 0.
 * The reseller portal carries the same component (`PortCountField.tsx`).
 */
export function normalizePortText(raw: string): string {
  return raw.replace(/\D/g, "").replace(/^0+(?=\d)/, "")
}

export function portCountFromText(text: string): number {
  return text === "" ? 0 : Number(text)
}

export function PortCountField({
  value,
  onValueChange,
  ...props
}: Omit<TextFieldProps, "value" | "onChange" | "type"> & {
  value: number
  onValueChange: (value: number) => void
}) {
  const [text, setText] = useState(String(value))

  // A change from outside (the draft loaded, a duplicate filled in) replaces
  // the text; our own edits already agree with `value`, so they are left be.
  useEffect(() => {
    setText((current) => (portCountFromText(current) === value ? current : String(value)))
  }, [value])

  return (
    <TextField
      {...props}
      type="text"
      value={text}
      inputProps={{ inputMode: "numeric", pattern: "[0-9]*", ...props.inputProps }}
      onChange={(event) => {
        const next = normalizePortText(event.target.value)
        setText(next)
        onValueChange(portCountFromText(next))
      }}
      onBlur={(event) => {
        if (text === "") setText("0")
        props.onBlur?.(event)
      }}
    />
  )
}
